import React, { useEffect, useState } from 'react';
import { Box, Grid, Paper, Typography, Tabs, Tab, useMediaQuery, useTheme } from '@mui/material';
import { makeStyles } from "tss-react/mui";
import GeneralSection from '../Components/PagesComponents/Settings/General/GeneralSection';
import RolePermissions from '../Components/PagesComponents/Settings/UserRoleManagement/RolePermissions';
import ButtonContained from '../Components/Common/ButtonContained';
import StageOfClaims from '../Components/PagesComponents/Settings/StageOfClaims';
import Account from '../Components/PagesComponents/Settings/Account/Account';
import Faq from '../Components/PagesComponents/Settings/Faq/Faq';
import Banner from '../Components/PagesComponents/Settings/Banner/Banner';
import GeneralNotification from '../Components/PagesComponents/Settings/GeneralNotification/GeneralNotification';
import { useLocation, useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import { showLoader } from '../Redux/Slices/loaderSlice';
import { getAvailableAccess } from '../Redux/Actions/action';
import { useDispatch } from 'react-redux';
import { checkAccess } from '../Utils/handler';
import Occupation from '../Components/PagesComponents/Settings/Occupation/Occupation';
import AppVersion from '../Components/PagesComponents/Settings/AppVersion/AppVersion';
import { useSearchParams } from 'react-router-dom';
import AppRolePermission from '../Components/PagesComponents/Settings/AppRolePermission/AppRolePermission';
import ConfigSetting from '../Components/PagesComponents/Settings/ConfigSetting/ConfigSetting';

const useStyles = makeStyles()((theme) => {
  return {
    verticalTab: {
      '&.MuiTabs-vertical ': {
        borderRight: '0px solid'
      },
      '& .MuiTab-root ': {
        backgroundColor: "#fff",
        textTransform: 'capitalize',
        alignItems: 'flex-start',
        marginBottom: '1px',
        borderRadius: '0px',
      },
      '& .MuiTab-root.Mui-selected ': {
        backgroundColor: '#3E4094',
        color: '#fff'
      },
      '& .MuiTabs-indicator ': {
        width: '0px'
      }
    },
    tab: {
      textAlign: 'left',
      fontWeight: 500,

      borderRadius: '4px',
      marginBottom: '8px',
    }
  };
});

const Settings = () => {
  // ---------- Hook -----------
  const { classes } = useStyles();
  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const dataAccess = useSelector((value) => value?.allAvailableAccess?.AllAvailableAccessList);


  const tabItems = [
    { Content: <GeneralSection />, accessKey: 'General' },
    { Content: <Banner />, accessKey: 'Banner' },
    { Content: <RolePermissions />, accessKey: 'User & Role Management' },
    { Content: <StageOfClaims />, accessKey: 'Stage Of Claims' },
    { Content: <Box><Typography>PPSK ID Content</Typography></Box>, accessKey: 'PPSK ID' },
    { Content: <ConfigSetting />, accessKey: 'Config Setting' },
    { Content: <Box><Typography>SMS Gateway Content</Typography></Box>, accessKey: 'SMS Gateway' },
    { Content: <GeneralNotification />, accessKey: 'Reminder Settings' },
    { Content: <Box><Typography>Age Wise Payment Setting Content</Typography></Box>, accessKey: 'Age Wise Payment Settings' },
    { Content: <Account />, accessKey: 'Account' },
    { Content: <Faq />, accessKey: 'FAQ' },
    { Content: <Occupation />, accessKey: 'Occupations' },
    { Content: <AppVersion />, accessKey: 'App Version' },
    { Content: <AppRolePermission />, accessKey: 'App Role & Permission' },
  ];
  const accessibleTabs = tabItems?.filter((tab) => checkAccess(pathname, dataAccess, tab?.accessKey));
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = Number(searchParams.get("tab")) || 0;

  // --------------- State -------------
  const [selectedTab, setSelectedTab] = useState(initialTab);
  const [showMenu, setShowMenu] = useState(true);

  // --------------- handleChange ---------------
  const handleTabChange = (e, newValue) => {
    setSelectedTab(newValue);
    setSearchParams({ tab: newValue.toString() });
    if (isMdDown) {
      setShowMenu(false);
    }
  };

  const toggleMenu = () => {
    setShowMenu(!showMenu);
  };

  // ---------- useEffect ------------ 
  useEffect(() => {
    dispatch(showLoader(true));
    dispatch(getAvailableAccess())
      .unwrap()
      .catch((error) => console.error("Error fetching access data", error))
      .finally(() => dispatch(showLoader(false)));
  }, [dispatch]);

  return (
    <Grid container spacing={2}>
      {/* Title Section */}
      <Grid item xs={12} display="flex" justifyContent="space-between">
        <Typography sx={{ fontSize: { lg: '22px', md: '18px' }, fontWeight: 600 }}>
          Settings
        </Typography>
        {isMdDown && (
          <ButtonContained
            height="30px"
            text={showMenu ? "Show Content" : "View Menu"}
            onClick={toggleMenu}
          />
        )}
      </Grid>

      <Grid item xs={12}>
        <Paper elevation={0} sx={{ borderRadius: '24px', padding: '24px' }}>
          <Grid container spacing={2}>
            {showMenu || !isMdDown ? (
              /* Vertical    */
              <Grid item xs={12} md={3} lg={3} xl={2}>
                <Box sx={{ backgroundColor: '#f8f8f8', padding: '18px', borderRadius: '8px' }}>
                  <Tabs
                    className={classes.verticalTab}
                    orientation="vertical"
                    value={selectedTab}
                    onChange={handleTabChange}
                    sx={{ borderRight: 1, borderColor: 'divider', minWidth: '150px' }}
                  >
                    {accessibleTabs?.map((tab, index) => (
                      <Tab
                        key={tab?.accessKey}
                        label={tab?.accessKey}
                        value={index}
                        className={classes.tab}
                        sx={{
                          '&:hover': {
                            color: selectedTab === index ? 'white' : 'black',
                            backgroundColor: selectedTab === index ? '#4A3AFF' : '#f0f0f0',
                          },
                          backgroundColor: selectedTab === index ? '#4A3AFF' : 'transparent',
                        }}
                      />
                    ))}
                  </Tabs>
                </Box>
              </Grid>
            ) : null}

            {/* Tab Content */}
            {!showMenu || !isMdDown ? (
              <Grid item xs={12} md={9} lg={9} xl={10}>
                {accessibleTabs[selectedTab]?.Content}
              </Grid>
            ) : null}
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default Settings;
